import { useState, useEffect, useRef } from 'react';

import {
  abortRequest,
  addThirdInQuote, calculateDiscount as calculateDiscountService, createQuote, getPlansQuoteData,
} from '../services/clientServices';
import { requestCalculatePlan, requestThird, requestValidation } from '../services/requests';
import { getRequestPlansQuote } from '../utils/requests-cotizacions';
import { calculateAge } from '../utils/helpers';
import { useAppContext } from '../AppProvider';

export const useQuote = (plans) => {
  const [currentPlans, setCurrentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    dispatch,
    state: {
      client,
      leadData,
      address,
    },
  } = useAppContext();

  const abortController = useRef({ signal: null, abort: abortRequest });

  useEffect(() => {
    getPlans(abortController);

    return () => {
      abortController.current?.abort(abortController.current?.signal);
    };
  }, []);

  const getPlans = async () => {
    const sendSignal = (signal) => {
      abortController.current.signal = signal;
    };
    setLoading(true);
    try {
      const resp = await getPlansQuoteData(getRequestPlansQuote({ leadData, client }, sendSignal));
      const list = plans
        .filter((_) => resp.plans.some((p) => p.nroPlanRS === Number(_.nroPlanRS)))
        .map((item) => {
          const newPlan = {
            ...item,
            idePlanSAS: resp.plans.find((p) => p.nroPlanRS === Number(item.nroPlanRS))?.idePlanSAS ?? null,
          };
          return newPlan;
        });
      getQuotes(list);
    } catch (error) {
      setCurrentPlans([]);
      setLoading(false);
    }
  };

  const getQuotes = async (list) => {
    try {
      const insureds = [
        {
          parentesco: 0,
          edad: calculateAge(client.fechaNacimiento),
        },
      ];
      const resp = await createQuote(
        requestValidation(
          insureds,
          list.map((_) => _.idePlanSAS),
        ),
      );
      const matchList = list.map((_) => ({
        ..._,
        price: resp.rptaCalculoPrima.find((q) => q.nroPlan === _.nroPlanRS)?.mtoCuota,
      }));
      getThirdInQuote(resp.ideCotizacion, matchList);
    } catch (error) {
      setCurrentPlans([]);
      setLoading(false);
    }
  };

  const getThirdInQuote = async (quoteId, list) => {
    try {
      const request = {
        idQuote: quoteId,
        client,
        address,
      };
      await addThirdInQuote(requestThird(request));
      calculateDiscount(quoteId, list);
    } catch (err) {
      setCurrentPlans([]);
      setLoading(false);
    }
  };

  const calculateDiscount = async (quoteId, list) => {
    try {
      const [resp] = await calculateDiscountService(requestCalculatePlan(quoteId));
      const matchList = list.map((_) => {
        const item = resp.rptaCalculoPrima.find((p) => p.nroPlan === _.nroPlanRS);
        if (!item) {
          return;
        }

        const priceDiscount = item?.descuentos.length > 0 && item?.mtoCuota && _.price !== item?.mtoCuota ? item?.mtoCuota : 0;
        return {
          ..._,
          priceDiscount,
          discount: item?.descuentos ?? [],
          idePlan: item.idePlan,
          idePlanOriginal: item.idePlanOriginal,
          nroPlan: item.nroPlan,
        };
      });
      dispatch({ type: 'SAVE_ID_QUOTE', payload: resp.ideCotizacion });
      setCurrentPlans(matchList.filter((_) => _));
    } catch (error) {
      setCurrentPlans([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    currentPlans,
  };
};
